import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Save, RotateCcw, Plus, Trash2 } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const Page = styled.div`min-height:100vh;background:#0a0a0f;color:#e2e8f0;font-family:'Inter','Pretendard',sans-serif;padding:20px;`;
const TopBar = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:28px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;`;
const BackBtn = styled.button`background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;&:hover{background:#1e2030;color:#e2e8f0}`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;background:linear-gradient(135deg,#8b5cf6,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;
const NavTabs = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
const NavTab = styled.button`
  background:${p => p.active ? 'rgba(139,92,246,0.15)' : 'transparent'};
  border:1px solid ${p => p.active ? '#8b5cf6' : '#1e2030'};
  color:${p => p.active ? '#8b5cf6' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;
  &:hover{border-color:#8b5cf6;color:#8b5cf6}
`;

const Section = styled.div`background:#111827;border:1px solid #1e2030;border-radius:14px;padding:20px;margin-bottom:16px;animation:${fadeIn} .4s ease;`;
const SectionTitle = styled.h3`margin:0 0 16px;font-size:14px;font-weight:700;color:#8b5cf6;`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:12px;`;
const Field = styled.div``;
const Label = styled.label`display:block;font-size:11px;color:#64748b;margin-bottom:4px;font-weight:600;`;
const Input = styled.input`
  width:100%;padding:8px 12px;background:#0d1117;border:1px solid #374151;border-radius:8px;color:#e2e8f0;font-size:13px;
  &:focus{outline:none;border-color:#8b5cf6}
`;
const Select = styled.select`
  width:100%;padding:8px 12px;background:#0d1117;border:1px solid #374151;border-radius:8px;color:#e2e8f0;font-size:13px;
  &:focus{outline:none;border-color:#8b5cf6}
`;
const Toggle = styled.label`
  display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;
  input{width:18px;height:18px;accent-color:#8b5cf6}
`;
const Btn = styled.button`
  display:flex;align-items:center;gap:6px;padding:10px 20px;border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;border:none;
  background:${p => p.danger ? 'rgba(239,68,68,0.15)' : p.primary ? 'rgba(139,92,246,0.2)' : '#1e2030'};
  color:${p => p.danger ? '#ef4444' : p.primary ? '#8b5cf6' : '#94a3b8'};
  border:1px solid ${p => p.danger ? '#ef4444' : p.primary ? '#8b5cf6' : '#374151'};
  &:hover{opacity:.85}&:disabled{opacity:.4}
`;

const WatchlistRow = styled.div`
  display:flex;align-items:center;gap:8px;padding:8px;background:#0d1117;border-radius:8px;margin-bottom:6px;font-size:13px;
`;

function MinerviniSettingsPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [newName, setNewName] = useState('');
  const [newMarket, setNewMarket] = useState('KOSPI');

  useEffect(() => {
    (async () => {
      try { setConfig(await apiService.getMinerviniConfig()); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const update = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));
  const updateNum = (key, val) => update(key, Number(val));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateMinerviniConfig(config);
      alert('✅ 설정이 저장되었습니다.');
    } catch (e) { alert('오류: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('⚠️ 모든 포지션을 초기화합니다. 계속할까요?')) return;
    try { await apiService.resetMinerviniAll(); alert('✅ 초기화 완료'); }
    catch (e) { alert('오류: ' + e.message); }
  };

  const addToWatchlist = () => {
    if (!newSymbol.trim() || !newName.trim()) return;
    const wl = [...(config.watchlist || []), { symbol: newSymbol.trim(), name: newName.trim(), market: newMarket }];
    update('watchlist', wl);
    setNewSymbol(''); setNewName('');
  };

  const removeFromWatchlist = (idx) => {
    const wl = [...(config.watchlist || [])];
    wl.splice(idx, 1);
    update('watchlist', wl);
  };

  if (loading) return <Page><div style={{ textAlign: 'center', padding: 60, color: '#475569' }}>로딩 중...</div></Page>;
  if (!config) return <Page><div style={{ textAlign: 'center', padding: 60, color: '#ef4444' }}>설정 로드 실패</div></Page>;

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/minervini')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>⚙️ Minervini VCP 설정</PageTitle>
          <PageSub>3단계 필터 + 매수/매도 파라미터 조정</PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/minervini')}>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/journal')}>거래일지</NavTab>
          <NavTab onClick={() => navigate('/labs/minervini/screener')}>스크리너</NavTab>
          <NavTab active onClick={() => navigate('/labs/minervini/settings')}><Settings size={13} /></NavTab>
        </NavTabs>
      </TopBar>

      {/* 계좌 설정 */}
      <Section>
        <SectionTitle>📊 계좌 설정</SectionTitle>
        <Grid>
          <Field><Label>초기 자금 (원)</Label><Input type="number" value={config.accountEquity} onChange={e => updateNum('accountEquity', e.target.value)} /></Field>
          <Field><Label>최대 포지션 수</Label><Input type="number" value={config.maxPositions} onChange={e => updateNum('maxPositions', e.target.value)} /></Field>
          <Field><Label>거래당 리스크 (%)</Label><Input type="number" step="0.01" value={config.riskPerTrade * 100} onChange={e => updateNum('riskPerTrade', e.target.value / 100)} /></Field>
          <Field><Label>기본 손절 (%)</Label><Input type="number" value={config.stopLossPercent} onChange={e => updateNum('stopLossPercent', e.target.value)} /></Field>
          <Field>
            <Label>조건 강도 (모드)</Label>
            <Select value={config.mode || 'HIGH'} onChange={e => update('mode', e.target.value)}>
              <option value="HIGH">상 (기본: 엄격함)</option>
              <option value="MEDIUM">중 (테스트: 완화됨)</option>
              <option value="LOW">하 (테스트: 매우 완화됨)</option>
            </Select>
          </Field>
          <Field><Toggle><input type="checkbox" checked={config.isActive} onChange={e => update('isActive', e.target.checked)} /> 자동매매 활성화</Toggle></Field>
        </Grid>
      </Section>

      {/* 1차: Trend Template */}
      <Section>
        <SectionTitle>📐 1차 필터: Trend Template (8가지 규칙)</SectionTitle>
        <Grid>
          <Field><Label>200일선 최소 상승 일수</Label><Input type="number" value={config.ma200MinUptrendDays} onChange={e => updateNum('ma200MinUptrendDays', e.target.value)} /></Field>
          <Field><Label>200일선 이상적 상승 일수</Label><Input type="number" value={config.ma200IdealUptrendDays} onChange={e => updateNum('ma200IdealUptrendDays', e.target.value)} /></Field>
          <Field><Label>52주 저점대비 최소 상승 (%)</Label><Input type="number" value={config.minDistFromLow52w} onChange={e => updateNum('minDistFromLow52w', e.target.value)} /></Field>
          <Field><Label>52주 고점대비 최대 이격 (%)</Label><Input type="number" value={config.maxDistFromHigh52w} onChange={e => updateNum('maxDistFromHigh52w', e.target.value)} /></Field>
          <Field><Label>RS 최소 점수</Label><Input type="number" value={config.minRelativeStrength} onChange={e => updateNum('minRelativeStrength', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* 2차: VCP */}
      <Section>
        <SectionTitle>🔄 2차 필터: VCP 패턴</SectionTitle>
        <Grid>
          <Field><Label>ZigZag 최소 변동 (%)</Label><Input type="number" value={config.zigzagMinChange} onChange={e => updateNum('zigzagMinChange', e.target.value)} /></Field>
          <Field><Label>최소 수축 횟수 (T)</Label><Input type="number" value={config.minContractions} onChange={e => updateNum('minContractions', e.target.value)} /></Field>
          <Field><Label>최대 수축 횟수 (T)</Label><Input type="number" value={config.maxContractions} onChange={e => updateNum('maxContractions', e.target.value)} /></Field>
          <Field><Label>1차 조정 최소 깊이 (%)</Label><Input type="number" value={config.minFirstContraction} onChange={e => updateNum('minFirstContraction', e.target.value)} /></Field>
          <Field><Label>1차 조정 최대 깊이 (%)</Label><Input type="number" value={config.maxFirstContraction} onChange={e => updateNum('maxFirstContraction', e.target.value)} /></Field>
          <Field><Label>수축 비율 상한</Label><Input type="number" step="0.05" value={config.contractionShrinkMax} onChange={e => updateNum('contractionShrinkMax', e.target.value)} /></Field>
          <Field><Label>마지막 수축 최대 깊이 (%)</Label><Input type="number" value={config.maxLastContraction} onChange={e => updateNum('maxLastContraction', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* VDU */}
      <Section>
        <SectionTitle>📉 거래량 (VDU)</SectionTitle>
        <Grid>
          <Field><Label>평균 거래량 기간 (일)</Label><Input type="number" value={config.vduAvgPeriod} onChange={e => updateNum('vduAvgPeriod', e.target.value)} /></Field>
          <Field><Label>VDU 비율 임계값</Label><Input type="number" step="0.05" value={config.vduRatioThreshold} onChange={e => updateNum('vduRatioThreshold', e.target.value)} /></Field>
          <Field><Label>연속 VDU 최소 일수</Label><Input type="number" value={config.vduMinDays} onChange={e => updateNum('vduMinDays', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* 매수/매도 */}
      <Section>
        <SectionTitle>🎯 매수/매도 설정</SectionTitle>
        <Grid>
          <Field><Label>돌파 거래량 배수</Label><Input type="number" step="0.05" value={config.breakoutVolumeMultiple} onChange={e => updateNum('breakoutVolumeMultiple', e.target.value)} /></Field>
          <Field><Label>피벗 허용 범위 (%)</Label><Input type="number" step="0.01" value={config.pivotTolerance * 100} onChange={e => updateNum('pivotTolerance', e.target.value / 100)} /></Field>
          <Field><Label>추적 손절 MA 기간</Label><Input type="number" value={config.trailingStopMA} onChange={e => updateNum('trailingStopMA', e.target.value)} /></Field>
          <Field><Label>이격 과다 비율</Label><Input type="number" step="0.05" value={config.overextendedRatio} onChange={e => updateNum('overextendedRatio', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* Power Play */}
      <Section>
        <SectionTitle>⚡ Power Play</SectionTitle>
        <Grid>
          <Field><Toggle><input type="checkbox" checked={config.enablePowerPlay} onChange={e => update('enablePowerPlay', e.target.checked)} /> Power Play 모듈 활성화</Toggle></Field>
          <Field><Label>8주 급등 기준 (%)</Label><Input type="number" value={config.powerPlayReturnThreshold} onChange={e => updateNum('powerPlayReturnThreshold', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* 감시종목 */}
      <Section>
        <SectionTitle>👁️ 감시 종목 ({config.watchlist?.length || 0}개)</SectionTitle>
        {(config.watchlist || []).map((item, idx) => (
          <WatchlistRow key={idx}>
            <span style={{ fontWeight: 600, minWidth: 80 }}>{item.symbol}</span>
            <span style={{ flex: 1 }}>{item.name}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{item.market}</span>
            <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}
              onClick={() => removeFromWatchlist(idx)}><Trash2 size={14} /></button>
          </WatchlistRow>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Input style={{ flex: '0 0 100px' }} placeholder="종목코드" value={newSymbol} onChange={e => setNewSymbol(e.target.value)} />
          <Input style={{ flex: 1 }} placeholder="종목명" value={newName} onChange={e => setNewName(e.target.value)} />
          <Select style={{ width: 100 }} value={newMarket} onChange={e => setNewMarket(e.target.value)}>
            <option value="KOSPI">KOSPI</option><option value="KOSDAQ">KOSDAQ</option>
          </Select>
          <Btn onClick={addToWatchlist}><Plus size={14} /> 추가</Btn>
        </div>
      </Section>

      {/* 버튼 */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <Btn primary onClick={handleSave} disabled={saving}><Save size={14} />{saving ? '저장 중...' : '설정 저장'}</Btn>
        <Btn danger onClick={handleReset}><RotateCcw size={14} /> 전체 초기화</Btn>
      </div>
    </Page>
  );
}

export default MinerviniSettingsPage;
